// src/dashboard/pages/teacher/Objectifs/components/ObjectifsFiltersCard.jsx

import React from "react";
import { FaPlus, FaSearch, FaSyncAlt } from "react-icons/fa";

import {
  getAdherentDisplayName,
  OBJECTIF_TYPES,
  OBJECTIF_TYPE_LABELS,
} from "../utils/objectifsDashboardUtils";

export default function ObjectifsFiltersCard({
  filters,
  setFilters,
  groups = [],
  adherents = [],
  seasonOptions = [],
  loading,
  onAdd,
  onRefresh,
}) {
  const updateFilter = (key, value) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        [key]: value,
      };

      if (key === "group") {
        next.adherentId = "";
      }

      return next;
    });
  };

  const resetFilters = () => {
    setFilters((prev) => ({
      ...prev,
      query: "",
      group: "",
      adherentId: "",
      type: "",
      status: "",
    }));
  };

  const hasFilters =
    String(filters.query || "").trim() ||
    filters.group ||
    filters.adherentId ||
    filters.type ||
    filters.status;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Recherche & filtres
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Affiner les objectifs
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Filtrez par saison, groupe, adhérent, type d’objectif ou état
              d’avancement.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Actualiser
            </button>

            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-[#012f6c]"
            >
              <FaPlus />
              Nouvel objectif
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-6">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Recherche
            </label>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={filters.query}
                onChange={(event) => updateFilter("query", event.target.value)}
                placeholder="Titre, adhérent..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Saison
            </label>

            <select
              value={filters.season}
              onChange={(event) => updateFilter("season", event.target.value)}
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              {seasonOptions.map((season) => (
                <option key={season} value={season}>
                  {season}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Groupe
            </label>

            <select
              value={filters.group}
              onChange={(event) => updateFilter("group", event.target.value)}
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">Tous les groupes</option>
              {groups.map((group) => (
                <option key={group.id || group.nom} value={group.nom}>
                  {group.nom}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Adhérent
            </label>

            <select
              value={filters.adherentId}
              onChange={(event) =>
                updateFilter("adherentId", event.target.value)
              }
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">Tous les adhérents</option>
              {adherents.map((adherent) => (
                <option key={adherent.id} value={adherent.id}>
                  {getAdherentDisplayName(adherent)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Type
            </label>

            <select
              value={filters.type}
              onChange={(event) => updateFilter("type", event.target.value)}
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">Tous les types</option>
              {OBJECTIF_TYPES.map((type) => (
                <option key={type} value={type}>
                  {OBJECTIF_TYPE_LABELS[type] || type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Statut
            </label>

            <select
              value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">Tous</option>
              <option value="todo">En cours</option>
              <option value="done">Terminés</option>
            </select>
          </div>
        </div>

        {hasFilters ? (
          <div className="mt-4">
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}